/**
 * Dashboard layout component — route-level shell.
 *
 * Mounts the app sidebar and wraps the routed `<Outlet />` with a
 * full-bleed wrapper. Pages own their own padding, scroll, max-width,
 * etc. Pages that need the previously-built-in padding wrapper now own
 * a small `page-layout` div at their root
 * (e.g. `Projects.tsx`).
 *
 * IMPORTANT: this shell is mounted ONCE at the dashboard route group
 * level (see `AppRoutes.tsx`) — not per-route. Mounting it once
 * preserves React + motion `LayoutGroup` context across cross-route
 * navigations so the `SidebarRail` active indicator can slide
 * smoothly between any two dashboard routes.
 *
 * Layout: a flexbox row (`<aside>` + `<main>`) that lets the sidebar
 * declare its own intrinsic width and the main outlet absorb the
 * remaining space. The shell does NOT know the sidebar's width — that
 * concern lives entirely inside `AppSidebar`.
 *
 * Always rendered at the route level via `element: <DashboardLayout />`
 * — never as a content wrapper inside a page. The shell composes pages,
 * not the other way around.
 *
 * Example (from `AppRoutes.tsx`):
 *  ```tsx
 *  { element: <DashboardLayout />, children: [...] }
 *  ```
 */

import { motion, useReducedMotion } from 'motion/react';

import { sidebarPanelSpring } from '@/shared/constants/motion.constants';
import { AppSidebar } from '../AppSidebar';
import { NavTransitionOutlet } from './NavTransitionOutlet';
import { useIsAppSidebarHidden } from '../../model/app-chrome';
import { useResolveWorkspacePermissions } from '../../lib/useResolveWorkspacePermissions';

/**
 * Props bag intentionally kept empty. Re-exported as a type so
 * downstream barrels and tests can probe the public surface even though
 * the component takes no props today. If the shell ever needs a knob
 * again, add it here so the API stays extensible without a breaking
 * import-shape change.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type DashboardLayoutProps = {};

export const DashboardLayout = () => {
  useResolveWorkspacePermissions();
  const isSidebarHidden = useIsAppSidebarHidden();
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="flex h-dvh bg-theme-background">
      {/* Hidden rather than unmounted: the sidebar keeps its motion `LayoutGroup` members and its
          queries across the trip into a full-window view and back, which is the same reason the
          shell itself is mounted once. */}
      {shouldReduceMotion ? (
        <aside className="shrink-0 z-10" hidden={isSidebarHidden}>
          <AppSidebar />
        </aside>
      ) : (
        /* `inert` rather than `hidden` while this animates: `hidden` is `display: none`, which
           leaves no box to take the width away from. The rail keeps its full width inside the
           clip, so it slides out whole rather than compressing on the way. */
        <motion.aside
          className="shrink-0 z-10 overflow-hidden"
          inert={isSidebarHidden}
          initial={false}
          animate={{ width: isSidebarHidden ? 0 : 'auto' }}
          transition={sidebarPanelSpring}
        >
          <AppSidebar />
        </motion.aside>
      )}
      <main className="flex-1 min-w-0 h-dvh">
        <div className="h-full">
          <NavTransitionOutlet />
        </div>
      </main>
    </div>
  );
};
